import SbButton from '@Components/Button';
import Modal from '@Components/Modal';
import UploadKyc from '@Components/ShipmentKycUpload/UploadKyc';
import { postFedExDocument } from '@Reducers/AdminShipment/actions';
import { getShipmentKyc } from '@Reducers/Shipment';
import { ShipmentKycType } from '@Reducers/Shipment/Type';
import { AppDispatch, RootStateType } from '@Store';
import AddIcon from '@mui/icons-material/Add';
import { Grid, Typography } from '@mui/material';
import { styled } from '@mui/system';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SingleDocument from './SingleDocument';
import { Dayjs } from 'dayjs';

interface PropsType {
  open: boolean;
  shipmentNumber: string;
  handleClose?: () => void;
  csp: string;
  type: string;
  emiratesDetails: any;
}

const SingleDocumentWrapper = styled(Grid)(() => ({
  border: '1px solid #b2b2b2',
  borderRadius: '8px',
}));

function UploadEmiratesDoc(props: PropsType) {
  const { open, handleClose, shipmentNumber, csp, type } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { shipmentKyc, isShipmentKycAdded, isShipmentKycDeleted } = useSelector(
    (state: RootStateType) => state.shipment,
  );
  const [uploadDocumentModal, setUploadDocumentModal] = useState<boolean>(false);
  const [singleDoc, setSingleDoc] = useState<ShipmentKycType | null>(null);
  const [documentType, setDocumentType] = useState<string>('');
  const [document, setDocument] = useState<ShipmentKycType | null>(null);

  // New state for commercial invoice fields
  const [commInvoiceNumber, setCommInvoiceNumber] = useState<string>('');
  const [commInvoiceDate, setCommInvoiceDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (shipmentNumber) {
      dispatch(getShipmentKyc(shipmentNumber));
    }
  }, [shipmentNumber]);

  useEffect(() => {
    if (isShipmentKycAdded) {
      console.log('isShipmentKycAdded : ', isShipmentKycAdded);
      setUploadDocumentModal(false);
      dispatch(getShipmentKyc(shipmentNumber));
    }
  }, [isShipmentKycAdded]);

  useEffect(() => {
    if (isShipmentKycDeleted) {
      dispatch(getShipmentKyc(shipmentNumber));
    }
  }, [isShipmentKycDeleted]);

  const uploadDocumentModalClose = () => setUploadDocumentModal(false);
  const uploadDocumentModalOpen = () => setUploadDocumentModal(true);

  const onDocumentUploadModalOpen = useCallback(
    (_documentLabel: string, documentData: ShipmentKycType | null): void => {
      setSingleDoc(documentData);
      uploadDocumentModalOpen();
    },
    [],
  );

  const handleFileChecked = (data: ShipmentKycType) => {
    setDocument(data);
    setDocumentType('');
    // Reset commercial invoice fields when new document is selected
    setCommInvoiceNumber('');
    setCommInvoiceDate(null);
  };

  const onUpload = () => {
    // Validate commercial invoice fields if document type is CommercialInvoice
    if (documentType === 'CommercialInvoice') {
      if (!commInvoiceNumber.trim() || !commInvoiceDate) {
        alert('Please fill all commercial invoice fields');
        return;
      }
    }

    // Prepare the base payload
    const basePayload = {
      trackingNumber: shipmentNumber,
      documentId: document?._id || '',
      documentType: documentType,
      csp,
      type,
    };

    // Add commercial invoice fields if document type is CommercialInvoice
    const finalPayload =
      documentType === 'CommercialInvoice'
        ? {
            ...basePayload,
            commInvoiceNumber,
            commInvoiceDate: commInvoiceDate ? commInvoiceDate.format('YYYY-MM-DD') : '',
          }
        : {
            ...basePayload,
          };

    dispatch(postFedExDocument(finalPayload));
  };

  // Check if upload button should be disabled
  const isUploadDisabled = () => {
    if (!document || !documentType) return true;

    // For CommercialInvoice, check if additional fields are filled
    if (documentType === 'CommercialInvoice') {
      return !commInvoiceNumber.trim() || !commInvoiceDate;
    }

    return false;
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        dialogContainerSx={{
          width: { xs: 'unset', md: '700px' },
          maxHeight: '80vh',
        }}
      >
        <Grid container sx={{ mb: 4, flexDirection: 'column', alignItems: 'center' }}>
          <Typography className="md-text font-semibold ">Documents For Shipment Number: {shipmentNumber}</Typography>
        </Grid>
        <UploadKyc
          open={uploadDocumentModal}
          onClose={uploadDocumentModalClose}
          documentData={singleDoc}
          shipmentNumber={shipmentNumber}
          allowedFileTypes={['image/png', 'image/jpeg', 'application/pdf']}
        />

        <SingleDocumentWrapper container item>
          {shipmentKyc?.length > 0 && (
            <Grid
              container
              item
              p={2.5}
              sx={{
                borderBottom: '1px solid',
              }}
              className={`border-grey`}
              gap={2}
            >
              <Grid className="font-medium" item xs={12} md={4.5} alignItems="center" pl={2}>
                Name
              </Grid>
              <Grid className="font-medium" item xs={12} md={3.5} alignItems="center">
                Documents
              </Grid>
              <Grid className="font-medium" item xs={12} md={2} alignItems="center">
                Last Sent
              </Grid>
            </Grid>
          )}

          {shipmentKyc.map((kyc, index) => (
            <SingleDocument
              key={`${kyc.name}-${index}`}
              label={kyc.documentName}
              required={false}
              documentData={kyc || null}
              handleFileChecked={handleFileChecked}
              selectedDocument={document}
              setDocumentType={setDocumentType}
              documentType={documentType}
              // Pass commercial invoice props
              setCommInvoiceNumber={setCommInvoiceNumber}
              setCommInvoiceDate={setCommInvoiceDate}
              commInvoiceNumber={commInvoiceNumber}
              commInvoiceDate={commInvoiceDate}
            />
          ))}
        </SingleDocumentWrapper>
        <AddNewKycButton onClick={() => onDocumentUploadModalOpen('', null)} variant="outlined" className="w-full">
          <AddIcon />
          <Typography>Add New Document</Typography>
        </AddNewKycButton>
        <SbButton
          onClick={onUpload}
          disabled={isUploadDisabled()}
          variant="contained"
          sx={{
            borderRadius: '8px !important',
            mt: 5,
            mb: 1,
          }}
          className="w-full"
        >
          Upload to Emirates
        </SbButton>
      </Modal>
    </>
  );
}

export default UploadEmiratesDoc;

export const AddNewKycButton = styled(SbButton)(({ theme }) => ({
  borderRadius: '8px !important',
  borderStyle: 'dashed',
  borderColor: theme.palette.border.grey,
  color: theme.palette.text.light,
  '&:hover': {
    borderStyle: 'dashed',
    borderColor: theme.palette.border.grey,
  },
  marginTop: '30px',
}));
